
import { defineComponent, reactive, ref, watch } from 'vue'
import { BannerView, CoursesView, JourneyView, FooterView, ToastSocialSharing } from '@/views'
import { useI18nStore } from '@/store/i18n.store'
import {
  failReenrollCommissionPurchase,
  failReenrollProductPurchase,
  failTransferPurchase,
  getReenrollStatus,
  approveReenrollCommissionPurchase,
  approveReenrollProductPurchase,
  approveTransferPurchase
} from '@/api/self-management.api'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: 'HomeView',
  components: {
    BannerView,
    CoursesView,
    JourneyView,
    FooterView,
    ToastSocialSharing
  },
  setup() {
    const i18nStore = useI18nStore()
    const loaded = ref(i18nStore.loaded)
    const tLoaded = ref(false)
    const { t } = useI18n()
    const toast = reactive({ toggle: false, label: '', type: '' })

    i18nStore.$subscribe((mutation: any, state: any) => {
      if (!tLoaded.value) {
        tLoaded.value = state.loaded
        hasReenrollRequestInProcess()
      }
    })

    watch([tLoaded], () => {
      if (tLoaded.value) loaded.value = true
    })

    const handlePayment = ({ isSuccess = false, msg = '' }) => {
      if (!tLoaded.value) return
      if (isSuccess) {
        toast.label = t(msg)
        toast.type = 'success'
        toast.toggle = true
      } else {
        toast.label = t(msg)
        toast.type = 'danger'
        toast.toggle = true
      }

      if (process.env.NODE_ENV !== 'development') localStorage.removeItem('ch-reenroll-request')
    }

    const hasReenrollRequestInProcess = async () => {
      const urlSearchParams = new URLSearchParams(window.location.search)
      const getReenrollParam = Object.fromEntries(urlSearchParams.entries())
      const commissionReenroll = getReenrollParam['flag-commission-reenroll']
      const productReenroll = getReenrollParam['flag-course-reenroll']
      //transfer logic
      const transferCommission = getReenrollParam['flag-transfer-commission']
      const transferCourse = getReenrollParam['flag-transfer-course']

      const refreshCoursesEvent = new CustomEvent('refresh-courses', { bubbles: true, detail: [{}] })

      if (commissionReenroll || productReenroll || transferCommission || transferCourse) {
        try {
          if (getReenrollParam.pid) {
            if (commissionReenroll) {
              if (Number(commissionReenroll)) {
                await approveReenrollCommissionPurchase(getReenrollParam)
                handlePayment({ isSuccess: true, msg: 'ch-modal-request-commission-reenroll.toast.paid' })

                document.dispatchEvent(refreshCoursesEvent)
              } else {
                await failReenrollCommissionPurchase(getReenrollParam)
                handlePayment({ isSuccess: false, msg: 'ch-modal-request-commission-reenroll.toast.fail' })
                throw 'err-reenroll-product-fail'
              }
            } else if (productReenroll) {
              if (Number(productReenroll)) {
                await approveReenrollProductPurchase(getReenrollParam)
                handlePayment({ isSuccess: true, msg: 'ch-modal-request-course-reenroll.toast.paid' })

                document.dispatchEvent(refreshCoursesEvent)
              } else {
                await failReenrollProductPurchase(getReenrollParam)
                handlePayment({ isSuccess: false, msg: 'ch-modal-request-course-reenroll.toast.fail' })
                throw 'err-reenroll-product-fail'
              }
            } else if (transferCommission || transferCourse) {
              if (Number(transferCommission || transferCourse)) {
                await approveTransferPurchase(getReenrollParam)
                handlePayment({ isSuccess: true, msg: 'ch-modal-request-transfer.toast.paid' })

                document.dispatchEvent(refreshCoursesEvent)
              } else {
                await failTransferPurchase(getReenrollParam)
                handlePayment({ isSuccess: false, msg: 'ch-modal-request-transfer.toast.fail' })
                throw 'err-transfer-fail'
              }
            }
          }
        } catch (err) {
          console.error('err-SELF_MANAGEMENT_V2', err)
        }
      }

      const isReenrollSuccess = Number(getReenrollParam['flag-reenroll'])

      let reenrollData: any = localStorage.getItem('ch-reenroll-request')

      if (reenrollData) reenrollData = JSON.parse(reenrollData)

      if (isReenrollSuccess && reenrollData?.purchaseId) {
        reenrollData.pid = reenrollData.response?.external_reference || reenrollData.purchaseId
        reenrollData.collection_id = getReenrollParam.collection_id
        reenrollData.payment_id = getReenrollParam.payment_id
        if (Number(isReenrollSuccess)) {
          try {
            const { success } = await getReenrollStatus(reenrollData)
            if (success) handlePayment({ isSuccess: success, msg: 'ch-modal-request-reactivation.toast.paid' })
            if (!success) throw new Error()
          } catch (error) {
            console.log(error)
            handlePayment({ isSuccess: false, msg: t('toast.error') })
          }
        } else {
          handlePayment({ isSuccess: false, msg: t('toast.error') })
        }
      }
    }

    return {
      loaded,
      toast
    }
  }
})
